/**
 * Resolve a raw NATS subject or Doris sensor_name to a channel key, using the
 * explicit matchers in each {@link ChannelSpec}. Matching is normalised
 * (lower-case, alphanumeric only) and considers both the final path segment and
 * its post-underscore tail (edge-16 nests names like `BU03_Humidity`).
 */

import type { ChannelSpec } from './spec';

function normalise(segment: string): string {
  return segment.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Map a NATS subject to a channel key, or null. */
export function resolveSubjectChannel(subject: string, channels: ChannelSpec[]): string | null {
  const parts = subject.split('.');
  const last = parts[parts.length - 1];
  const lastNorm = normalise(last);
  const us = last.lastIndexOf('_');
  const tailNorm = us !== -1 ? normalise(last.slice(us + 1)) : lastNorm;

  for (const ch of channels) {
    if (ch.nats?.regex) {
      try {
        if (new RegExp(ch.nats.regex).test(subject)) return ch.key;
      } catch {
        /* invalid regex in spec — ignore */
      }
    }
    for (const alias of ch.nats?.aliases ?? []) {
      const na = normalise(alias);
      if (na === lastNorm || na === tailNorm) return ch.key;
    }
  }
  return null;
}

/** Map a Doris sensor_name to a channel key, or null. */
export function resolveSensorChannel(sensorName: string, channels: ChannelSpec[]): string | null {
  const us = sensorName.lastIndexOf('_');
  const lastSeg = us !== -1 ? sensorName.slice(us + 1) : sensorName;
  const lastNorm = normalise(lastSeg);

  for (const ch of channels) {
    const d = ch.doris;
    if (!d) continue;
    // Component-scoped query returns sibling axes too; keep only this axis.
    if (d.axisToken && !sensorName.includes(`_${d.axisToken}_`)) continue;
    for (const alias of d.sensorAliases ?? []) {
      if (normalise(alias) === lastNorm) return ch.key;
    }
    if (d.prefix && sensorName.startsWith(d.prefix)) return ch.key;
  }
  return null;
}

/**
 * Distinct Doris `sensor_prefix` LIKE-prefixes to query for a set of channels.
 * Falls back to '%' when channels don't declare prefixes (resolve client-side).
 */
export function dorisPrefixes(channels: ChannelSpec[]): string[] {
  const set = new Set<string>();
  for (const ch of channels) {
    if (ch.doris?.prefix) set.add(ch.doris.prefix);
  }
  return set.size > 0 ? Array.from(set) : ['%'];
}
